// src/pages/EditUserModal.jsx

import React, { useState, useRef, useEffect } from "react";
import { db, storage } from "../firebase";
import { doc, updateDoc, setDoc, getDoc, arrayUnion, serverTimestamp } from "firebase/firestore"; 
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useAuth } from "../context/AuthContext"; 
import { FaTimes, FaCamera, FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";
import { resolveEmployeeRecordRef } from "../utils/employeeRecord";
import { sumGrossBreakdown, computeTotalCTC, computeGrossBreakdown, parseCTCNumber, splitRetentionTranches, fmtINR } from "../utils/salaryUtils";
import "./EditUserModal.css";

const EditUserModal = ({ user, onClose, onUserUpdated }) => {
  const { userData: adminData } = useAuth(); 
  const isSuperAdmin = adminData?.role === 'superadmin';

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);
  const [department, setDepartment] = useState(user.department || "");
  const [position, setPosition] = useState(user.position || "");  
  const [joiningDate, setJoiningDate] = useState(user.joiningDate || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [employeeId, setEmployeeId] = useState(user.employeeId || user.uid);
  const [loading, setLoading] = useState(false);
  const originalRole = useRef(user.role); // track initial role for change detection
  // --- ✨ NEW BANK/STATUTORY STATES (from user prop) ---
  const [location, setLocation] = useState(user.location || "");
  const [bankName, setBankName] = useState(user.bankName || "");
  const [bankAccountNumber, setBankAccountNumber] = useState(user.bankAccountNumber || "");  const [ifscCode, setIfscCode] = useState(user.ifscCode || "");  const [panNumber, setPanNumber] = useState(user.panNumber || "");
  const [pfNumber, setPfNumber] = useState(user.pfNumber || "");
  const [uanNumber, setUanNumber] = useState(user.uanNumber || "");  const [reportingManager, setReportingManager] = useState(user.reportingManager || "Hanumat Lal Vishwakarma (Founder & Managing Director)");
  const [photoURL, setPhotoURL] = useState(user.photoURL || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user.photoURL || "");
  const profilePicInputRef = useRef(null);

  // ── Fixed salary breakdown + variable components — superadmin-only edit ──
  // users collection doesn't carry these; fetch the full record from employee_records_complete.
  const [basic, setBasic] = useState("");
  const [hra, setHra] = useState("");
  const [lta, setLta] = useState("");
  const [medical, setMedical] = useState("");
  const [telephone, setTelephone] = useState("");
  const [special, setSpecial] = useState("");
  const [joiningBonus, setJoiningBonus] = useState("");
  const [variablePayPool, setVariablePayPool] = useState("");
  const [retentionPool, setRetentionPool] = useState("");
  const [salaryLoading, setSalaryLoading] = useState(true);
  // Gross/CTC as currently stored, used to detect real salary changes
  const storedSalary = useRef({ gross: 0, ctc: 0 });

  useEffect(() => {
    const empId = user.employeeId || user.id;
    if (!empId) { setSalaryLoading(false); return; }
    (async () => {
      try {
        const { snap } = await resolveEmployeeRecordRef(empId, user.uid || user.id);
        const d = snap.exists() ? snap.data() : {};
        let b = d.salaryBreakdown || {};
        const v = d.variableComponents || {};
        const log = Array.isArray(d.appraisalLog) ? d.appraisalLog : [];
        // Stored gross may have been zeroed by an earlier save, so fall back to users doc and log history
        const lastLogGross = [...log].reverse()
          .flatMap(a => [parseCTCNumber(String(a.newMonthlyGross || 0)), parseCTCNumber(String(a.oldMonthlyGross || 0))])
          .find(n => n > 0) || 0;
        const knownGross = parseCTCNumber(String(d.grossMonthlySalary || 0))
          || parseCTCNumber(String(user.grossMonthlySalary || 0))
          || lastLogGross;
        storedSalary.current = {
          gross: parseCTCNumber(String(d.grossMonthlySalary || 0)),
          ctc: parseCTCNumber(String(d.annualCTC || 0)),
        };
        if (!(Number(b.basic) > 0) && knownGross > 0) b = computeGrossBreakdown(knownGross) || {};
        setBasic(String(b.basic || ""));
        setHra(String(b.hra || ""));
        setLta(String(b.lta || ""));
        setMedical(String(b.medical || ""));
        setTelephone(String(b.telephone || ""));
        setSpecial(String(b.special || ""));
        setJoiningBonus(String(v.joiningBonus || ""));
        setVariablePayPool(String(v.variablePayPool || ""));
        setRetentionPool(String(v.retentionPool || ""));
      } catch (err) {
        console.warn("Could not load salary breakdown:", err);
      }
      setSalaryLoading(false);
    })();
  }, [user.employeeId, user.id]);

  const newBreakdown = {
    basic: parseFloat(basic) || 0, hra: parseFloat(hra) || 0, lta: parseFloat(lta) || 0,
    medical: parseFloat(medical) || 0, telephone: parseFloat(telephone) || 0, special: parseFloat(special) || 0,
  };
  const newVariableComponents = {
    joiningBonus: parseFloat(joiningBonus) || 0,
    variablePayPool: parseFloat(variablePayPool) || 0,
    retentionPool: parseFloat(retentionPool) || 0,
  };
  const { monthlyGross } = sumGrossBreakdown(newBreakdown);
  const totalCTC = computeTotalCTC(newBreakdown, newVariableComponents);

  // Handle profile picture selection
  const handleProfilePicChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setPhotoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload profile picture to Firebase Storage
  const uploadProfilePicture = async () => {
    if (!photoFile) return photoURL; // Return existing URL if no new file

    try {
      setUploadingPhoto(true);
      const timestamp = Date.now();
      const storageRef = ref(storage, `profile_pictures/${user.id}_${timestamp}`);
      
      const uploadTask = uploadBytesResumable(storageRef, photoFile);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload progress:', progress);
          },
          (error) => {
            console.error('Upload error:', error);
            setUploadingPhoto(false);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadingPhoto(false);
            resolve(downloadURL);
          }
        );
      });
    } catch (error) {
      setUploadingPhoto(false);
      throw error;
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    setLoading(true);
    const toastId = toast.loading("Updating user profile...");

    try {
      // Upload profile picture if a new one is selected
      let newPhotoURL = photoURL;
      if (photoFile) {
        toast.loading("Uploading profile picture...", { id: toastId });
        newPhotoURL = await uploadProfilePicture();
      }

      // Update Firestore document
      toast.loading("Saving changes to database...", { id: toastId });
      console.log("Updating user document for:", user.id);
      
      const updateData = {
        name,
        email,
        role,
        department,
        position,
        joiningDate,
        phone,
        employeeId,
        location,
        bankName,
        bankAccountNumber,
        ifscCode,
        panNumber,
        pfNumber,
        uanNumber,
        reportingManager,
        photoURL: newPhotoURL,
      };
      // Never overwrite stored salary with zeros when the breakdown is empty
      const writeSalary = isSuperAdmin && monthlyGross > 0;
      if (writeSalary) {
        updateData.grossMonthlySalary = String(monthlyGross);
        updateData.annualCTC = String(totalCTC);
      }

      const userDocRef = doc(db, "users", user.id);
      await updateDoc(userDocRef, updateData);

      // Also update employee_records_complete and track role changes
      const empId = user.employeeId || user.id;
      const empRecordUpdate = {
        name, email, role, department, position,
        joiningDate, phone, location,
        bankName, bankAccountNumber, ifscCode,
        panNumber, pfNumber, uanNumber,
        reportingManager,
        photoURL: newPhotoURL,
        updatedAt: serverTimestamp(),
      };
      if (role !== originalRole.current) {
        empRecordUpdate.roleLog = arrayUnion({
          date:        new Date().toISOString().split("T")[0],
          oldRole:     originalRole.current,
          newRole:     role,
          oldPosition: user.position || "",
          newPosition: position.trim(),
          note:        "Role updated via profile edit",
          promotedBy:  adminData?.name || adminData?.email || "admin",
          promotedAt:  new Date().toISOString(),
        });
      }
      // Superadmin-only: direct salary breakdown edit, bypassing the appraisal hike-% flow
      if (writeSalary) {
        empRecordUpdate.salaryBreakdown = newBreakdown;
        empRecordUpdate.variableComponents = { ...newVariableComponents, ...splitRetentionTranches(newVariableComponents.retentionPool) };
        empRecordUpdate.grossMonthlySalary = String(monthlyGross);
        empRecordUpdate.annualCTC = String(totalCTC);
        if (monthlyGross !== storedSalary.current.gross || totalCTC !== storedSalary.current.ctc) {
          empRecordUpdate.appraisalLog = arrayUnion({
            date:            new Date().toISOString().split("T")[0],
            oldCTC:          String(storedSalary.current.ctc),
            newCTC:          String(totalCTC),
            oldMonthlyGross: String(storedSalary.current.gross),
            newMonthlyGross: String(monthlyGross),
            note:            "Salary structure revised",
            revisedBy:       "HR",
            revisedAt:       new Date().toISOString(),
          });
        }
      }
      try {
        // merge-set: creates the record if it was never created (updateDoc would fail silently here)
        const { ref: empRef } = await resolveEmployeeRecordRef(empId, user.uid || user.id);
        await setDoc(empRef, {
          employeeId: empId,
          uid: user.uid || user.id,
          ...empRecordUpdate,
        }, { merge: true });
      } catch (empErr) {
        console.error("Could not sync to employee_records_complete:", empErr);
        toast.error(`Profile saved, but employee record sync failed: ${empErr.message}`);
      }


      console.log("✅ User profile updated successfully");
      toast.success("User profile updated successfully!", { id: toastId });
      
      // Important: Reset loading before callbacks
      setLoading(false);
      
      // Call parent callbacks
      if (onUserUpdated) onUserUpdated();
      if (onClose) onClose();
      
    } catch (err) {
      console.error("❌ Update User Error:", err);
      toast.error(`Failed to update user: ${err.message}`, { id: toastId });
    } finally {
      // Always reset loading state
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-wrapper">
        <div className="modal-content edit-user-modal-content">
          <button className="close-btn" onClick={onClose} data-tooltip="Close"><FaTimes /></button>
          <h2>✏️ Edit User: {user.name}</h2>
          
          <form onSubmit={handleUpdateUser}>
            
            {/* === SECTION 0: Profile Picture === */}
            <div className="profile-pic-section">
              <div className="profile-pic-container-modal">
                <div className="profile-pic-wrapper-modal">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Profile" className="profile-pic-preview-modal" />
                  ) : (
                    <div className="profile-pic-placeholder-modal">
                      <FaCamera size={40} />
                      <p>No photo</p>
                    </div>
                  )}
                  <button
                    type="button"
                    className="profile-pic-edit-btn-modal"
                    onClick={() => profilePicInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    title="Change profile picture"
                  >
                    {uploadingPhoto ? <FaSpinner className="spin" /> : <FaCamera />}
                  </button>
                </div>
                <input
                  ref={profilePicInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePicChange}
                  style={{ display: 'none' }}
                />
                <p className="profile-pic-hint">Click camera icon to change photo (Max 5MB)</p>
              </div>
            </div>
            
            {/* === SECTION 1: Basic Information === */}
            <h4>📋 Basic Information</h4>
            <div className="edit-user-form-grid">
              <div className="edit-user-field">
                <label htmlFor="edit-name">Full Name <span className="required-mark">*</span></label>
                <input id="edit-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="edit-user-field">
                <label htmlFor="edit-email">Email Address</label>
                <input id="edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled />
              </div>
            </div>

            <div className="edit-user-form-grid">
              <div className="edit-user-field">
                <label htmlFor="edit-role">Role</label>
                <select id="edit-role" value={role} onChange={(e) => setRole(e.target.value)} disabled={adminData?.role !== 'superadmin'}>
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="edit-user-field">
                <label htmlFor="edit-employeeId">Employee ID</label>
                <input id="edit-employeeId" type="text" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
              </div>
            </div>
            
            {/* === SECTION 2: Job Details === */}
            <h4>💼 Job Information</h4>
            <div className="edit-user-form-grid">
              <div className="edit-user-field">
                <label htmlFor="edit-joiningDate">Joining Date</label>
                <input id="edit-joiningDate" type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} />
              </div>
              <div className="edit-user-field">
                <label htmlFor="edit-dept">Department</label>
                <input id="edit-dept" type="text" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g., Technology" />
              </div>
            </div>

            <div className="edit-user-form-grid single-column">
              <div className="edit-user-field">
                <label htmlFor="edit-pos">Position</label>
                <input id="edit-pos" type="text" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g., Content Creator" />
              </div>
            </div>

            <div className="edit-user-form-grid single-column">
              <div className="edit-user-field">
                <label htmlFor="edit-reportingManager">Reporting Manager</label>
                <input id="edit-reportingManager" type="text" value={reportingManager} onChange={(e) => setReportingManager(e.target.value)} placeholder="Hanumat Lal Vishwakarma (Founder & Managing Director)" />
                <small style={{ color: '#888', fontSize: '0.78rem' }}>Default: Hanumat Lal Vishwakarma (Founder &amp; Managing Director)</small>
              </div>
            </div>

            {/* === SECTION 3: Contact Details === */}
            <h4>📞 Contact Information</h4>
            <div className="edit-user-form-grid">
              <div className="edit-user-field">
                <label htmlFor="edit-phone">Phone Number</label>
                <input id="edit-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 XXXXXXXXXX" />
              </div>
              <div className="edit-user-field">
                <label htmlFor="edit-location">Location</label>
                <input id="edit-location" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, State" />
              </div>
            </div>
            
            {/* === SECTION 4: Bank & Statutory Details === */}
            <h4>🏦 Bank & Statutory Details</h4>
            <div className="edit-user-form-grid">
              <div className="edit-user-field">
                <label htmlFor="edit-bankName">Bank Name</label>
                <input id="edit-bankName" type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g., HDFC Bank" />
              </div>
              <div className="edit-user-field">
                <label htmlFor="edit-bankAccountNumber">Bank Account Number</label>
                <input id="edit-bankAccountNumber" type="text" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="Account number" />
              </div>
            </div>

            <div className="edit-user-form-grid">
              <div className="edit-user-field">
                <label htmlFor="edit-ifscCode">IFSC Code</label>
                <input id="edit-ifscCode" type="text" value={ifscCode} onChange={(e) => setIfscCode(e.target.value)} placeholder="e.g., HDFC0001234" />
              </div>
              <div className="edit-user-field">
                {/* Empty field for grid alignment */}
              </div>
            </div>

            <div className="edit-user-form-grid">
              <div className="edit-user-field">
                <label htmlFor="edit-panNumber">PAN Number</label>
                <input id="edit-panNumber" type="text" value={panNumber} onChange={(e) => setPanNumber(e.target.value)} placeholder="ABCDE1234F" />
              </div>
              <div className="edit-user-field">
                <label htmlFor="edit-pfNumber">PF Number</label>
                <input id="edit-pfNumber" type="text" value={pfNumber} onChange={(e) => setPfNumber(e.target.value)} placeholder="PF account number" />
              </div>
            </div>

            <div className="edit-user-form-grid single-column">
              <div className="edit-user-field">
                <label htmlFor="edit-uanNumber">UAN</label>
                <input id="edit-uanNumber" type="text" value={uanNumber} onChange={(e) => setUanNumber(e.target.value)} placeholder="UAN number" />
              </div>
            </div>

            {/* === SECTION 5: Salary Breakdown (Superadmin-only direct edit) === */}
            <h4>💰 Salary Breakdown {!isSuperAdmin && <span style={{ fontSize: '0.72rem', color: '#9CA3AF', fontWeight: 400 }}>(Superadmin only — view only for you)</span>}</h4>
            {salaryLoading ? (
              <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>Loading saved breakdown…</p>
            ) : (
              <>
                <p style={{ color: '#9CA3AF', fontSize: '0.78rem', margin: '-6px 0 8px' }}>
                  Direct edit — bypasses the Appraisal hike-% flow. For everyday appraisals, prefer the Appraise button on the employee profile.
                </p>
                <div className="edit-user-form-grid">
                  <div className="edit-user-field">
                    <label htmlFor="edit-basic">Basic (Monthly)</label>
                    <input id="edit-basic" type="number" value={basic} disabled={!isSuperAdmin} onChange={(e) => setBasic(e.target.value)} />
                  </div>
                  <div className="edit-user-field">
                    <label htmlFor="edit-hra">HRA</label>
                    <input id="edit-hra" type="number" value={hra} disabled={!isSuperAdmin} onChange={(e) => setHra(e.target.value)} />
                  </div>
                </div>
                <div className="edit-user-form-grid">
                  <div className="edit-user-field">
                    <label htmlFor="edit-lta">LTA</label>
                    <input id="edit-lta" type="number" value={lta} disabled={!isSuperAdmin} onChange={(e) => setLta(e.target.value)} />
                  </div>
                  <div className="edit-user-field">
                    <label htmlFor="edit-medical">Medical Allowance</label>
                    <input id="edit-medical" type="number" value={medical} disabled={!isSuperAdmin} onChange={(e) => setMedical(e.target.value)} />
                  </div>
                </div>
                <div className="edit-user-form-grid">
                  <div className="edit-user-field">
                    <label htmlFor="edit-telephone">Telephone &amp; Internet Allowance</label>
                    <input id="edit-telephone" type="number" value={telephone} disabled={!isSuperAdmin} onChange={(e) => setTelephone(e.target.value)} />
                  </div>
                  <div className="edit-user-field">
                    <label htmlFor="edit-special">Special Allowance</label>
                    <input id="edit-special" type="number" value={special} disabled={!isSuperAdmin} onChange={(e) => setSpecial(e.target.value)} />
                  </div>
                </div>
                <div className="edit-user-form-grid">
                  <div className="edit-user-field">
                    <label htmlFor="edit-joiningBonus">Joining / Relocation Bonus</label>
                    <input id="edit-joiningBonus" type="number" value={joiningBonus} disabled={!isSuperAdmin} onChange={(e) => setJoiningBonus(e.target.value)} />
                  </div>
                  <div className="edit-user-field">
                    <label htmlFor="edit-variablePool">Variable Pay Pool (Annual Max)</label>
                    <input id="edit-variablePool" type="number" value={variablePayPool} disabled={!isSuperAdmin} onChange={(e) => setVariablePayPool(e.target.value)} />
                  </div>
                </div>
                <div className="edit-user-form-grid single-column">
                  <div className="edit-user-field">
                    <label htmlFor="edit-retentionPool">Retention Bonus Pool (Total, splits into 5 yearly tranches)</label>
                    <input id="edit-retentionPool" type="number" value={retentionPool} disabled={!isSuperAdmin} onChange={(e) => setRetentionPool(e.target.value)} />
                  </div>
                </div>
                <div className="edit-user-form-grid">
                  <div className="edit-user-field">
                    <label>Monthly Gross (computed)</label>
                    <div style={{ padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '0.93rem', fontWeight: 600, color: '#374151' }}>
                      {monthlyGross > 0 ? `₹${fmtINR(monthlyGross)}` : '—'}
                    </div>
                  </div>
                  <div className="edit-user-field">
                    <label>Total CTC (computed)</label>
                    <div style={{ padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '0.93rem', fontWeight: 600, color: '#374151' }}>
                      {totalCTC > 0 ? `₹${fmtINR(totalCTC)}` : '—'}
                    </div>
                  </div>
                </div>
              </>
            )}

            <button type="submit" disabled={loading} className="primary-btn" data-tooltip={loading ? "Updating..." : "Update User Profile"}>
              {loading ? "⏳ Updating..." : "✅ Update User"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;